import type { Options } from '@sentry/core';
import { FetchyError } from '../fetchy';
import { SentryIgnoredError } from './sentry-ignored-error';

// Known Firestore SDK bug unrelated to app logic, floods Sentry with noise.
const FIRESTORE_INTERNAL_ASSERTION_PATTERN = /INTERNAL ASSERTION FAILED/i;

/**
 * Resolution order:
 * 1. Explicit override via NEXT_PUBLIC_SENTRY_ENVIRONMENT
 * 2. "production" catch-all when pointing at the production Firebase project
 * 3. Vercel-provided deployment context (production | preview | development)
 * 4. "local" fallback
 */
const environment =
  process.env.NEXT_PUBLIC_SENTRY_ENVIRONMENT ??
  (process.env.NEXT_PUBLIC_DATABASE === 'production' ? 'production' : undefined) ??
  process.env.NEXT_PUBLIC_VERCEL_ENV ??
  process.env.VERCEL_ENV ??
  'local';

export const commonConfig: Options = {
  environment,

  /**
   * This needs to be passed as env in the corresponding
   * next.config.js for the running application.
   */
  release: process.env.NEXT_PUBLIC_SENTRY_RELEASE,

  /**
   * 1% of production request are gonna be sampled
   * and no sampling at all in dev/vercel-preview since
   * the amount transactions for sampling is limited
   **/

  tracesSampleRate: process.env.NEXT_PUBLIC_DATABASE === 'production' ? 0.01 : 0.0,

  /**
   * Disabled by default, enable per sentry.{client,server,edge}.config.ts in
   * corresponding app directory in development if you need to debug Sentry.
   **/
  debug: false,

  /**
   * Allows to throw some errors without sending them to Sentry.
   */
  ignoreErrors: [SentryIgnoredError, FIRESTORE_INTERNAL_ASSERTION_PATTERN],

  /**
   * Allows to add custom fingerprint, tags, extra and contexts to errors before sending them to Sentry.
   * https://docs.sentry.io/platforms/javascript/configuration/options/#options-beforesend
   */
  beforeSend(event, hint) {
    const error = hint.originalException;

    if (error instanceof FetchyError) {
      event.contexts = {
        ...event.contexts,
        Fetchy: {
          URL: error.response?.url ?? error.endpoint,
          Code: error.code,
          Method: error.method,
          Endpoint: error.endpoint,
        },
      };

      event.fingerprint = [error.toString()];
    }

    return event;
  },
};
